import { useState, useEffect, useRef, useMemo } from 'react';
import { applyFormatting } from '../../utils/formatting';
import { parseHoraires, getStatus, formatHour, DAY_LABELS, WEEK_ORDER } from '../../utils/horaires';
import { useHoraire, useContactInfo, useNow } from '../../lib/customHooks';
import { MAPS_URL } from '../../utils/constants';
import './Presentation.scss';

const DEFAULT_BANNIERE = { type: 'video', url: '/assets/Home/video.mp4' };

const IconPin = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>
);
const IconClock = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
);
const IconPhone = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z"/></svg>
);
const IconMail = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>
);

function Presentation () {
    const horaire = useHoraire();
    const contact = useContactInfo();
    const now = useNow();
    const schedule = useMemo(() => parseHoraires(horaire), [horaire]);
    const status = getStatus(schedule, now);

    const [banniere, setBanniere] = useState(DEFAULT_BANNIERE);
    const [isMuted, setIsMuted] = useState(true);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const videoRef = useRef(null);

    useEffect(() => {
        fetch('/api/banniere')
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data && data.url) setBanniere({ type: data.type || 'video', url: data.url }); })
            .catch(() => {});
    }, []);

    const scrollToVrac = (e) => {
        const el = document.getElementById('vrac');
        if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const openDays = schedule ? WEEK_ORDER.filter((d) => schedule[d] && schedule[d].length) : [];

    return (
        <section className='hero-band'>
            <div className='hero'>
                <div className='hero-left'>
                    <img src='/assets/Home/logoBAsiqueLargeTranspRoanne.webp' alt='Logo BAsique, épicerie vrac | 0 déchet | Roanne' className='hero-logo' />
                    <h1 className='hero-tagline'>
                        Vos courses <span className='cursive'>bio</span> et <span className='cursive'>sans emballage</span>, en cœur de ville.
                    </h1>
                    <p className='hero-text'>
                        Une épicerie bio en vrac à Roanne, pour faire la plus grande partie de vos courses courantes tout en adoptant des pratiques zéro déchet.
                    </p>
                </div>

                <div className='hero-media'>
                    <span className='hero-media__label' aria-hidden='true'>Découvrez-nous</span>
                    {banniere.type === 'image' ? (
                        <img src={banniere.url} alt="Bandeau BAsique" className='bandeau-photo' />
                    ) : (
                        <>
                            <video
                                ref={videoRef}
                                src={banniere.url}
                                className='bandeau-photo'
                                loop
                                muted={isMuted}
                                playsInline
                                onPlay={() => setIsVideoPlaying(true)}
                                onPause={() => setIsVideoPlaying(false)}
                                aria-label="Vidéo BAsique"
                            />
                            <button
                                type="button"
                                className={`bandeau-play-btn ${isVideoPlaying ? 'bandeau-play-btn--playing' : ''}`}
                                onClick={() => {
                                    const videoEl = videoRef.current;
                                    if (!videoEl) return;
                                    if (videoEl.paused) videoEl.play();
                                    else videoEl.pause();
                                }}
                                aria-label={isVideoPlaying ? 'Mettre en pause' : 'Lancer la vidéo'}
                            >
                                {isVideoPlaying ? (
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <rect x="6" y="5" width="4" height="14"></rect>
                                        <rect x="14" y="5" width="4" height="14"></rect>
                                    </svg>
                                ) : (
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <polygon points="6 4 20 12 6 20 6 4"></polygon>
                                    </svg>
                                )}
                            </button>
                            <button
                                type="button"
                                className="bandeau-mute-btn"
                                onClick={() => setIsMuted((prev) => !prev)}
                                aria-label={isMuted ? 'Activer le son' : 'Couper le son'}
                            >
                                {isMuted ? (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                        <line x1="23" y1="9" x2="17" y2="15"></line>
                                        <line x1="17" y1="9" x2="23" y2="15"></line>
                                    </svg>
                                ) : (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                                    </svg>
                                )}
                            </button>
                        </>
                    )}
                </div>

                <div className='badges'>
                    <span className='badge'>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 19c0-8 5-13 14-14-1 9-6 14-14 14z"/><path d="M5 19l8-8"/></svg>Bio
                    </span>
                    <span className='badge'><img src='/assets/vrac-icon.webp' alt='' />Vrac</span>
                    <span className='badge'><img src='/assets/local-icon.webp' alt='' />Local</span>
                    <span className='badge'>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/></svg>0 déchet
                    </span>
                </div>
                <div className='hero-actions'>
                    <a className='btn' href='/#vrac' onClick={scrollToVrac}>Comment ça marche ?</a>
                    <a className='btn btn--outline' href={MAPS_URL} target='_blank' rel='noopener noreferrer'>
                        <IconPin />Itinéraire
                    </a>
                </div>
            </div>

            <div className='infocard-wrap'>
                <div className='infocard'>
                    <div className='infocell'>
                        <p className='infos-label'><IconPin />Adresse</p>
                        <p className='adresse'>
                            17 rue Anatole France
                            <small>42300 Roanne · juste en face du musée</small>
                        </p>
                        <a className='infocell__link' href={MAPS_URL} target='_blank' rel='noopener noreferrer'>Voir sur la carte</a>
                    </div>

                    <div className='infocell'>
                        <p className='infos-label'><IconClock />Horaires</p>
                        {openDays.length > 0 ? (
                            <div className='days'>
                                {openDays.map((d) => (
                                    <div key={d} className={`day ${now.getDay() === d ? 'day--today' : ''}`}>
                                        <b>{DAY_LABELS[d]}</b>
                                        {schedule[d].map(([a, b], i) => (
                                            <span key={i}>{formatHour(a)} – {formatHour(b)}</span>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className='horaire' dangerouslySetInnerHTML={{ __html: applyFormatting(horaire) }}></p>
                        )}
                    </div>

                    <div className='infocell infocell--status'>
                        <div className='status-main'>
                            <p className='infos-label'><IconClock />En ce moment</p>
                            {status ? (
                                <>
                                    <p className={`status-big ${status.open ? '' : 'status-big--closed'}`}><i></i><span>{status.text}</span></p>
                                    <p className='status-sub'>{status.sub}</p>
                                </>
                            ) : (
                                <p className='status-sub'>Voir les horaires ci-contre</p>
                            )}
                        </div>
                        <div className='status-contact'>
                            <a href={`tel:${contact.telephone.replace(/\D/g, '')}`} aria-label={`Appeler le ${contact.telephone}`}><IconPhone /><span>{contact.telephone}</span></a>
                            <a href={`mailto:${contact.email}`} aria-label={`Écrire à ${contact.email}`}><IconMail /><span>{contact.email}</span></a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Presentation;
